import {
  Button,
  NumberInput,
  Popover,
  Select,
  Switch,
  Text,
  UnstyledButton,
} from "@mantine/core";
import { ArrowLeft, Music, SlidersHorizontal, Video } from "lucide-react";
import { useId, useState, type ReactNode } from "react";

import {
  AUDIO_CODECS,
  AUDIO_FORMATS,
  CUSTOM_DEFAULT,
  FRAME_RATES,
  PRESET_GROUPS,
  RESOLUTIONS,
  VIDEO_CODECS,
  VIDEO_FORMATS,
  consistent,
  type AudioCodecName,
  type Container,
  type ExportSettings,
  type Preset,
  type VideoCodecName,
} from "./engine/presets";
import { liveFrame } from "./engine/project";
import type { AspectRatio } from "./project";
import classes from "./editor.module.css";

/** A preset row: its name, resolution and format, as live's rows show them; one click exports. */
function Row({ preset, onClick }: { preset: Preset; onClick: () => void }) {
  return (
    <UnstyledButton
      className={classes.presetRow}
      aria-label={`${preset.name} ${preset.resolution} ${preset.format}`}
      onClick={onClick}
    >
      <span className={classes.presetName}>{preset.name}</span>
      <span className={classes.presetFacts}>
        {preset.resolution}
        <span className={classes.presetFormat}>{preset.format}</span>
      </span>
    </UnstyledButton>
  );
}

/** A labelled field of the Custom view, as live's: the name on the left, the control on the right. */
function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className={classes.customField}>
      <Text className={classes.customLabel}>{label}</Text>
      {children}
    </div>
  );
}

/** One of the Custom view's two cards, Video or Audio: its icon, name and switch, then its fields. */
function Card(props: {
  icon: ReactNode;
  title: string;
  on: boolean;
  onToggle: (on: boolean) => void;
  children: ReactNode;
}) {
  return (
    <section className={classes.customCard} aria-label={props.title}>
      <div className={classes.customCardHead}>
        {props.icon}
        <Text className={classes.customCardTitle}>{props.title}</Text>
        <Switch
          checked={props.on}
          onChange={(event) => props.onToggle(event.currentTarget.checked)}
          aria-label={props.title}
          color="var(--cai-color-primary)"
        />
      </div>
      {props.on ? <div className={classes.customFields}>{props.children}</div> : null}
    </section>
  );
}

/** A select kept inside the popover (its list would close the popover from a portal). */
function Choice<T extends string>(props: {
  label: string;
  value: T;
  options: readonly { value: T; label: string }[];
  onChange: (value: T) => void;
}) {
  return (
    <Select
      aria-label={props.label}
      className={classes.customControl}
      data={props.options.map((option) => ({ value: option.value, label: option.label }))}
      value={props.value}
      allowDeselect={false}
      comboboxProps={{ withinPortal: false }}
      onChange={(value) => {
        const chosen = props.options.find((option) => option.value === value);
        if (chosen !== undefined) props.onChange(chosen.value);
      }}
    />
  );
}

/**
 * Live's Custom Export (`live-editor-export/04` to `06`): Video (Resolution, Bitrate, Codec,
 * Format, Frame Rate) and Audio (Codec, Sample Rate), each with its switch, then Export. A
 * choice keeps the rest consistent as live's view does; with Video off the file is a sound
 * file (MP3, WAV, FLAC, OGG), its codec its format's.
 */
function CustomView(props: {
  facts: string;
  onBack: () => void;
  onExport: (settings: ExportSettings) => void;
}) {
  const [settings, setSettings] = useState<ExportSettings>(CUSTOM_DEFAULT);
  // What the switches remember while off, so turning one back on restores it.
  const [video, setVideo] = useState(CUSTOM_DEFAULT.video!);
  const [audio, setAudio] = useState(CUSTOM_DEFAULT.audio!);
  const update = (next: ExportSettings) => {
    const kept = consistent(next);
    setSettings(kept);
    if (kept.video !== null) setVideo(kept.video);
    if (kept.audio !== null) setAudio(kept.audio);
  };
  const v = settings.video;
  const a = settings.audio;
  const resolution = RESOLUTIONS.find((each) => each.short === v?.short) ?? RESOLUTIONS[1]!;
  return (
    <>
      <div className={classes.exportHeader} data-custom>
        <div>
          <Text className={classes.exportTitle}>Custom Export</Text>
          <Text className={classes.exportFacts}>{props.facts}</Text>
        </div>
        <UnstyledButton className={classes.customBack} onClick={props.onBack}>
          <ArrowLeft size={14} aria-hidden /> Back
        </UnstyledButton>
      </div>
      <div className={classes.customBody}>
        <Card
          icon={<Video size={16} aria-hidden />}
          title="Video"
          on={v !== null}
          onToggle={(on) =>
            update({
              ...settings,
              video: on ? video : null,
              container: on ? "mp4" : "mp3",
              audio: settings.audio ?? (on ? null : audio),
            })
          }
        >
          {v === null ? null : (
            <>
              <Field label="Resolution">
                <Choice
                  label="Resolution"
                  value={resolution.label}
                  options={RESOLUTIONS.map((each) => ({ value: each.label, label: each.label }))}
                  onChange={(label) => {
                    const chosen = RESOLUTIONS.find((each) => each.label === label)!;
                    // As live's: a resolution resets the bitrate to its own.
                    update({
                      ...settings,
                      video: { ...v, short: chosen.short, bitrate: chosen.bitrate },
                    });
                  }}
                />
              </Field>
              <Field label="Bitrate (Mbps)">
                <NumberInput
                  aria-label="Bitrate (Mbps)"
                  className={classes.customControl}
                  min={1}
                  max={200}
                  step={1}
                  allowDecimal={false}
                  value={v.bitrate / 1_000_000}
                  onChange={(value) => {
                    const mbps = Math.min(200, Math.max(1, Number(value) || 1));
                    update({ ...settings, video: { ...v, bitrate: mbps * 1_000_000 } });
                  }}
                />
              </Field>
              <Field label="Codec">
                <Choice<VideoCodecName>
                  label="Video codec"
                  value={v.codec}
                  options={VIDEO_CODECS}
                  onChange={(codec) => update({ ...settings, video: { ...v, codec } })}
                />
              </Field>
              <Field label="Format">
                <Choice<Container>
                  label="Format"
                  value={settings.container}
                  options={VIDEO_FORMATS}
                  onChange={(container) => update({ ...settings, container })}
                />
              </Field>
              <Field label="Frame Rate">
                <Choice
                  label="Frame Rate"
                  value={String(v.fps)}
                  options={FRAME_RATES.map((each) => ({ value: String(each.value), label: each.label }))}
                  onChange={(fps) => update({ ...settings, video: { ...v, fps: Number(fps) } })}
                />
              </Field>
            </>
          )}
        </Card>
        <Card
          icon={<Music size={16} aria-hidden />}
          title="Audio"
          on={a !== null}
          onToggle={(on) => update({ ...settings, audio: on ? audio : null })}
        >
          {a === null ? null : (
            <>
              {v === null ? (
                <Field label="Format">
                  <Choice<Container>
                    label="Format"
                    value={settings.container}
                    options={AUDIO_FORMATS}
                    onChange={(container) => update({ ...settings, container })}
                  />
                </Field>
              ) : (
                <Field label="Codec">
                  <Choice<Exclude<AudioCodecName, "pcm">>
                    label="Audio codec"
                    value={a.codec === "pcm" ? "aac" : a.codec}
                    options={AUDIO_CODECS}
                    onChange={(codec) => update({ ...settings, audio: { ...a, codec } })}
                  />
                </Field>
              )}
              <Field label="Sample Rate">
                <Choice
                  label="Sample Rate"
                  value={String(a.sampleRate)}
                  options={[
                    { value: "44100", label: "44.1 kHz" },
                    { value: "48000", label: "48 kHz" },
                  ]}
                  onChange={(rate) =>
                    update({ ...settings, audio: { ...a, sampleRate: rate === "44100" ? 44_100 : 48_000 } })
                  }
                />
              </Field>
            </>
          )}
        </Card>
        <Button
          fullWidth
          className={classes.downloadButton}
          disabled={v === null && a === null}
          onClick={() => props.onExport(settings)}
        >
          Export
        </Button>
      </div>
    </>
  );
}

export interface ExportPopoverProps {
  aspectRatio: AspectRatio;
  totalMs: number;
  /** Busy with a save or an export starting: the button spins. */
  loading: boolean;
  /** Starts an export with a preset's settings or Custom's. */
  onExport: (settings: ExportSettings) => void;
  children: ReactNode;
}

/**
 * Live's "Download": a popover of export presets, each one click (no confirm, none chosen
 * first), and "Custom", which opens live's Custom Export view. The header names the length and
 * the project's size, as live's does (its frame, `LIVE_FRAME`).
 */
export function ExportPopover(props: ExportPopoverProps) {
  const { aspectRatio, totalMs, loading, onExport, children } = props;
  const [opened, setOpened] = useState(false);
  const [view, setView] = useState<"presets" | "custom">("presets");
  const title = useId();
  const size = liveFrame(aspectRatio);
  const seconds = `${(totalMs / 1000).toFixed(1)}s`;
  const exportNow = (settings: ExportSettings) => {
    setOpened(false);
    setView("presets");
    onExport(settings);
  };
  return (
    <Popover
      opened={opened}
      onChange={(next) => {
        setOpened(next);
        if (!next) setView("presets");
      }}
      position="bottom-end"
      width={320}
      shadow="md"
    >
      <Popover.Target>
        <Button
          className={classes.downloadButton}
          loading={loading}
          aria-haspopup="dialog"
          aria-expanded={opened}
          onClick={() => setOpened(!opened)}
        >
          {children}
        </Button>
      </Popover.Target>
      {/* Named by its own title, not by the button Mantine would name it after. */}
      <Popover.Dropdown className={classes.exportPanel} aria-labelledby={title}>
        {view === "custom" ? (
          <>
            <Text id={title} hidden>
              Custom Export
            </Text>
            <CustomView
              facts={`${size.width}×${size.height} · ${seconds}`}
              onBack={() => setView("presets")}
              onExport={exportNow}
            />
          </>
        ) : (
          <>
            <div className={classes.exportHeader}>
              <Text className={classes.exportTitle} id={title}>
                Export
              </Text>
              <Text className={classes.exportFacts}>
                {seconds} · {size.width}×{size.height}
              </Text>
            </div>
            <div className={classes.exportGroups}>
              {PRESET_GROUPS.map((group) => (
                <section key={group.label} aria-label={group.label}>
                  <Text className={classes.exportGroupLabel}>{group.label}</Text>
                  <div className={classes.presetGroup}>
                    {group.presets.map((preset) => (
                      <Row
                        key={preset.name + preset.format}
                        preset={preset}
                        onClick={() => exportNow(preset.settings)}
                      />
                    ))}
                  </div>
                </section>
              ))}
              <section aria-label="Advanced">
                <Text className={classes.exportGroupLabel}>Advanced</Text>
                <div className={classes.presetGroup}>
                  <UnstyledButton
                    className={classes.presetRow}
                    aria-label="Custom"
                    onClick={() => setView("custom")}
                  >
                    <span className={classes.presetName}>Custom</span>
                    <SlidersHorizontal size={14} aria-hidden />
                  </UnstyledButton>
                </div>
              </section>
            </div>
          </>
        )}
      </Popover.Dropdown>
    </Popover>
  );
}
